import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { theme as antTheme, App, Flex, Modal, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../../../../Components/Button"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { modalConfig } from "../../../../../theme"
import utils, { paymentProcessorTitle } from "../../../../../Utils/utils"
// Reuse the exact processor-page card design + request popup (/prevent/configure/processors)
// so the Integrate step is visually consistent with it.
import IntegrationRequestForm from "../../../../IntegrationsPage/components/IntegrationRequestForm"
import RequestSuccess from "../../../../IntegrationsPage/components/RequestSuccess"
import {
  disconnectPaymentProcessor,
  getPaymentProcessors
} from "../../../../User/redux/user.thunks"
import { requestIntegrationReset, selectBuildingBlocks } from "../redux/configure.slice"
import {
  getIntegrationCatalog,
  getProfile,
  getShopifyScopeStatus,
  requestIntegration
} from "../redux/configure.thunks"
import { setupLockReason } from "../utils/setupLock"

import "../../../../IntegrationsPage/components/Card/styles.less"
import cardStyles from "./integrationCard.module.less"

const { Text, Title } = Typography

// The Integrate step shows a single, ungrouped grid of the payment processors that
// support Recover's dispute sync, unlike /prevent/configure/processors which still groups
// its larger processor list into Payment processors / Subscriptions / Notifications
// sub-tabs. There is no Enrichment tab here; evidence-enrichment tools are configured
// elsewhere.
//
// Every Recover processor is always a card. The Recover backend's own catalog
// (/dispute-management/integrations/) is the source of connection state and connect links;
// the Prevent processors list only decorates the card (title, description, flags) when it
// happens to hold a row for the processor — it is not what decides whether a card exists.
const RECOVER_PROCESSOR_SLUGS = ["stripe", "shopify"]
const PROCESSOR_SLUGS = new Set(RECOVER_PROCESSOR_SLUGS)

// Payment-processor card. Same .integration-card design as /prevent/configure/processors,
// fed by the global processor connection (building_blocks.processors + user.paymentProcessors)
// so connecting here lights up everywhere the connection is shown.
const ProcessorCard = ({ proc, locked, onConnect, onDisconnect, onReconnect }) => {
  const { t } = useTranslation()
  const { token } = antTheme.useToken()
  return (
    <Flex vertical gap={16} className="integration-card rcv-integrate-card">
      <Flex align="center" justify="space-between">
        {proc.logo ? (
          <img
            src={proc.logo}
            alt={proc.title}
            width={32}
            height={32}
            className="integration-card-logo"
          />
        ) : (
          <div className={cardStyles.intCardIcon}>
            <Icon type="card" style={{ fontSize: 20 }} />
          </div>
        )}
        {proc.connected && proc.needsUpgrade ? (
          <Flex gap={4} align="center" className="mark-beta">
            <Icon type="warning" style={{ fontSize: 20 }} />
            <Text style={{ color: "currentColor" }}>{t("Reconnect needed")}</Text>
          </Flex>
        ) : proc.connected ? (
          <Flex gap={4} align="center" className="mark-connected">
            <Icon type="check_mark" style={{ fontSize: 20 }} />
            <Text style={{ color: "currentColor" }}>{t("Connected")}</Text>
          </Flex>
        ) : proc.isNew || proc.isBeta ? (
          <Flex align="center" gap={8}>
            {proc.isNew ? (
              <Flex gap={4} align="center" className="mark-new">
                <Text style={{ color: "currentColor", textTransform: "uppercase" }}>
                  {t("New")}
                </Text>
              </Flex>
            ) : null}
            {proc.isBeta ? (
              <Flex gap={4} align="center" className="mark-beta">
                <Text style={{ color: "currentColor" }}>{t("BETA")}</Text>
              </Flex>
            ) : null}
          </Flex>
        ) : null}
      </Flex>
      <div>
        <Flex gap={8} align="center" style={{ marginBottom: 8 }} wrap="nowrap">
          <Title level={5} style={{ lineHeight: "22px", margin: 0, whiteSpace: "nowrap" }}>
            {proc.title}
          </Title>
          {proc.connected && proc.account ? (
            <Text
              type="secondary"
              ellipsis={{ tooltip: proc.account }}
              style={{
                maxWidth: "100%",
                border: "1px solid",
                borderColor: token?.colorBorder,
                padding: "0 7px",
                borderRadius: token?.radiusBase
              }}
            >
              {proc.account}
            </Text>
          ) : null}
        </Flex>
        <Text type="secondary">{proc.desc}</Text>
      </div>
      {/* A shop connected before Recover asked for the dispute-evidence scopes can still
          be read for Alerts, so we do not treat it as disconnected — we ask for the extra
          grant, which upgrades the existing installation in place. */}
      {proc.connected && proc.needsUpgrade ? (
        <Text type="secondary">
          {t(
            "This connection predates dispute handling and is missing the permissions we need to read and submit evidence. Reconnect to grant them — nothing is reinstalled and your alerts keep working."
          )}
        </Text>
      ) : null}
      <Flex gap={12} vertical style={{ marginTop: "auto", width: "100%" }}>
        {proc.connected && proc.needsUpgrade ? (
          <>
            <Button
              type="primary"
              size="large"
              className="flex center"
              icon={<Icon type="plugin_connect" style={{ fontSize: 20 }} />}
              disabled={locked}
              onClick={() => onReconnect(proc)}
              text={t("Reconnect")}
            />
            <Button
              type="default"
              danger
              size="large"
              className="flex center"
              icon={<Icon type="circle_x" style={{ fontSize: 20 }} />}
              disabled={locked}
              onClick={() => onDisconnect(proc.slug)}
              text={t("Disconnect")}
            />
          </>
        ) : proc.connected ? (
          <Button
            type="default"
            danger
            size="large"
            className="flex center"
            icon={<Icon type="circle_x" style={{ fontSize: 20 }} />}
            disabled={locked}
            onClick={() => onDisconnect(proc.slug)}
            text={t("Disconnect")}
          />
        ) : proc.disabled ? (
          <Button
            type="default"
            size="large"
            disabled
            className="flex center"
            icon={<Icon type="calendar_clock" style={{ fontSize: 20 }} />}
            text={t(proc.disabledStatus || "Coming soon")}
          />
        ) : (
          <Button
            type="default"
            size="large"
            className="flex center"
            icon={<Icon type="plugin_connect" style={{ fontSize: 20 }} />}
            disabled={locked}
            onClick={() => onConnect(proc.slug)}
            text={t("Connect")}
          />
        )}
      </Flex>
    </Flex>
  )
}

// "Integration missing?" card. Same with-bg variant as the processors page; opens the
// shared request popup. Appended to the end of every tab.
const RequestCard = ({ onClick }) => {
  const { t } = useTranslation()
  return (
    <Flex vertical gap={16} className="integration-card with-bg rcv-integrate-card">
      <Flex align="center" justify="space-between">
        {Images.integration_missing ? (
          <img
            src={Images.integration_missing}
            alt=""
            width={32}
            height={32}
            className="integration-card-logo"
          />
        ) : (
          <div className={cardStyles.intCardIcon}>
            <Icon type="integrations" style={{ fontSize: 20 }} />
          </div>
        )}
      </Flex>
      <div>
        <Title level={5} style={{ lineHeight: "22px", margin: "0 0 8px" }}>
          {t("Integration missing?")}
        </Title>
        <Text type="secondary">
          {t(
            "Let our team know which integration you are missing and we will add it as soon as possible."
          )}
        </Text>
      </div>
      <Flex gap={12} vertical style={{ marginTop: "auto", width: "100%" }}>
        <Button
          type="default"
          size="large"
          className="flex center"
          icon={<Icon type="plus" style={{ fontSize: 20 }} />}
          onClick={onClick}
          text={t("Request integration")}
        />
      </Flex>
    </Flex>
  )
}

const IntegrationsTab = () => {
  const dispatch = useDispatch()
  const buildingBlocks = useSelector(selectBuildingBlocks)
  const paymentProcessors = useSelector((s) => s.user.paymentProcessors)
  const userInfo = useSelector((s) => s.auth.userInfo)
  const integrationRequest = useSelector((s) => s.disputesConfigure.integrationRequest)
  const shopifyScope = useSelector((s) => s.disputesConfigure.profile.shopifyScope)
  const integrationCatalog = useSelector((s) => s.disputesConfigure.profile.integrationCatalog)
  // Passed to RequestIntegrationForm as onSubmit, so keep it stable.
  const submitIntegrationRequest = useCallback(
    (data) => dispatch(requestIntegration(data)),
    [dispatch]
  )
  const { t } = useTranslation()
  const { modal, message } = App.useApp()
  const businessId = userInfo?.current_business_account_id
  // While onboarding is unfinished or the account is still in demo state, the step is
  // viewable but its processor actions are off; only the request form stays live.
  const lockReason = setupLockReason(userInfo)
  const locked = !!lockReason
  const lockNote =
    lockReason === "onboarding"
      ? t("Finish the Getting started steps to connect a payment processor.")
      : lockReason === "demo"
        ? t("Payment processors can be connected once your account is activated.")
        : null

  // "Integration missing?" request popup (reuses the processors-page form + success).
  const [requestOpen, setRequestOpen] = useState(false)
  const [requestDone, setRequestDone] = useState(false)
  const openRequest = () => {
    setRequestDone(false)
    setRequestOpen(true)
  }
  const closeRequest = () => {
    setRequestOpen(false)
    setRequestDone(false)
    if (integrationRequest?.done || integrationRequest?.error) dispatch(requestIntegrationReset())
  }
  // Flip to the success state once the request action reports back.
  useEffect(() => {
    if (requestOpen && integrationRequest?.done) setRequestDone(true)
  }, [integrationRequest?.done, requestOpen])

  // --- Payment processors (mirrors ProcessorConnect, rendered as cards) ----------------
  useEffect(() => {
    if (!paymentProcessors || paymentProcessors.length === 0)
      dispatch(getPaymentProcessors({ merchant_id: businessId }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connectedProcessors = useMemo(
    () =>
      Array.isArray(buildingBlocks?.processors?.connected)
        ? buildingBlocks.processors.connected
        : [],
    [buildingBlocks]
  )

  // Refetch the Recover profile the moment a processor newly connects so the cards,
  // the required_information gate and the locked descriptor all update without a
  // manual refresh.
  const liveConnectedCount = (paymentProcessors || []).filter(
    (p) => PROCESSOR_SLUGS.has(p.service_name) && p.is_connected
  ).length
  const prevConnectedCount = useRef(liveConnectedCount)
  useEffect(() => {
    if (liveConnectedCount > prevConnectedCount.current) dispatch(getProfile())
    prevConnectedCount.current = liveConnectedCount
  }, [liveConnectedCount, dispatch])

  // Re-check the shop's scopes whenever the connection count moves, so a fresh connect
  // (or a completed re-auth, which returns here) settles the card without a reload.
  useEffect(() => {
    dispatch(getShopifyScopeStatus())
    dispatch(getIntegrationCatalog())
  }, [liveConnectedCount, dispatch])

  const catalogBySlug = useMemo(() => {
    const out = {}
    for (const entry of integrationCatalog?.processors || []) out[entry.id] = entry
    return out
  }, [integrationCatalog])

  const titleForProcessor = (slug) =>
    (paymentProcessors || []).find((x) => x.service_name === slug)?.title ||
    paymentProcessorTitle[slug] ||
    slug

  // Flat card list for the Integrations tab. Each card's description and connected
  // account come straight from the processor's own data (matching the shared
  // IntegrationCard used in onboarding), not a generic per-group blurb.
  const processorCards = useMemo(() => {
    const buildCard = (slug) => {
      const p = (paymentProcessors || []).find((x) => x.service_name === slug)
      const c = catalogBySlug[slug]
      const title = titleForProcessor(slug)
      return {
        slug,
        title,
        desc:
          p?.description ||
          t("Connect your _name_ account to automatically sync and fight disputes.", {
            name: title
          }),
        logo: Images[`integration_${slug}`],
        // Three readings of "connected" exist (the Recover catalog, the profile's connected
        // list, the Prevent row) and a processor connected through any of them is connected;
        // none may veto another, or a fixture that sets only token rows reads as disconnected.
        connected: !!c?.connected || connectedProcessors.includes(slug) || !!p?.is_connected,
        account:
          c?.connected_account ||
          buildingBlocks?.processors?.[slug]?.account_label ||
          p?.connected_account,
        connectLink: p?.link || c?.connect_link || null,
        hasInvalidToken: !!p?.has_invalid_token || !!c?.has_invalid_token,
        isNew: !!p?.is_new,
        isBeta: !!p?.is_beta,
        disabled: !!p?.isDisabled,
        disabledStatus: p?.disabledStatus,
        // Only Shopify has a scope check today; Stripe's equivalent is the app-version
        // permission set and is tracked separately.
        needsUpgrade:
          slug === "shopify" &&
          ((!!shopifyScope?.checked && !!shopifyScope.needsUpgrade) || !!c?.needs_scope_upgrade),
        reauthUrl: slug === "shopify" ? shopifyScope?.reauthUrl : null
      }
    }
    return RECOVER_PROCESSOR_SLUGS.map(buildCard)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProcessors, connectedProcessors, buildingBlocks, shopifyScope, catalogBySlug, t])

  const handleConnectProcessor = (slug) => {
    if (locked) return
    const card = processorCards.find((x) => x.slug === slug)
    if (slug === "stripe" && card?.hasInvalidToken) {
      window.open("https://marketplace.stripe.com/", "_blank", "noopener,noreferrer")
      return
    }
    // Both Recover processors connect through the provider's OAuth page, so the only thing a
    // click can do is follow the link. With no link — no business account yet, or the catalog
    // has not answered — there is nothing to post; the Prevent-style POST that used to sit here
    // sent a body the backend never expected.
    if (card?.connectLink) {
      utils.goLink(card.connectLink)
      return
    }
    message.error(t("Could not start the connection. Please try again."))
  }

  // The re-auth URL is Shopify's own consent screen for the extra scopes. Falling back
  // to the normal connect link keeps the button useful if the check answered before the
  // backend could build one.
  const handleReconnectProcessor = (proc) => {
    if (proc.reauthUrl) {
      utils.goLink(proc.reauthUrl)

      return
    }

    handleConnectProcessor(proc.slug)
  }

  const handleDisconnectProcessor = (slug) => {
    if (locked) return
    const p = (paymentProcessors || []).find((x) => x.service_name === slug)
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: true,
      title: t("Disconnect _name_?", { name: titleForProcessor(slug) }),
      content: t(
        "We'll stop syncing disputes and reading evidence from this processor. You can reconnect any time."
      ),
      okText: t("Disconnect"),
      okButtonProps: { ...modalConfig("exclamation").okButtonProps, danger: true },
      cancelText: t("Cancel"),
      getContainer: () => document.getElementById("main-layout"),
      onOk: () =>
        dispatch(
          disconnectPaymentProcessor({
            service_name: slug,
            merchant_id: businessId,
            payment_processor_id: p?.id
          })
        )
    })
  }

  return (
    <div className="recover-section-form_integrations_inner">
      {/* Single flat grid, no tab switcher: this step only ever shows payment
          processors now (see the file-level comment above). */}
      {lockNote ? (
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          {lockNote}
        </Text>
      ) : null}
      <div className={cardStyles.intCardgrid}>
        {processorCards.map((proc) => (
          <ProcessorCard
            key={proc.slug}
            proc={proc}
            locked={locked}
            onConnect={handleConnectProcessor}
            onDisconnect={handleDisconnectProcessor}
            onReconnect={handleReconnectProcessor}
          />
        ))}
        <RequestCard onClick={openRequest} />
      </div>

      {/* "Integration missing?" request popup, identical to the Alerts one. */}
      <Modal
        open={requestOpen}
        centered
        width={442}
        title={requestDone ? null : t("Integration request")}
        footer={null}
        closeIcon={<Icon type="cross_small" />}
        closable
        onCancel={closeRequest}
        destroyOnClose
        getContainer={() => document.getElementById("main-layout") || document.body}
        classNames={{ content: cls("clear-bg full-width-header", { "bg-center": requestDone }) }}
      >
        {requestDone ? (
          <RequestSuccess onClose={closeRequest} />
        ) : (
          <div className="rcv-int-request">
            <Text type="secondary" style={{ display: "inline-block", marginBottom: 16 }}>
              {t(
                "Let our team know which integration you are missing and we will add it as soon as possible."
              )}
            </Text>
            {integrationRequest?.error ? (
              <Text type="danger" style={{ display: "inline-block", marginBottom: 12 }}>
                {integrationRequest.error}
              </Text>
            ) : null}
            <IntegrationRequestForm
              onClose={closeRequest}
              onSubmit={submitIntegrationRequest}
              loading={!!integrationRequest?.loading}
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default IntegrationsTab
